/**
 * @fileoverview Forgot Password Form Component
 * @module ForgotPasswordForm
 * 
 * Business Context:
 * - Allows users to request a password reset email
 * - Integrates with backend password reset API
 * 
 * Design Context:
 * - Consistent with LoginForm design
 * - Simple, focused UX for password recovery
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';

interface ForgotPasswordFormData {
  email: string;
}

const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
});

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ 
  onBackToLogin 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apiClient.auth.resetPassword(data.email);
      setIsSuccess(true);
    } catch (error) {
      // For security, we show success even if email doesn't exist
      // The backend handles this the same way
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="w-full max-w-md mx-auto text-center">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Check your email
          </h1>
          <p className="text-slate-600">
            If an account exists with that email, we've sent password reset instructions.
          </p>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Didn't receive the email? Check your spam folder or try again.
          </p>
          
          <button
            onClick={onBackToLogin}
            className="inline-flex items-center text-slate-900 hover:text-teal-600 font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          Reset your password
        </h1>
        <p className="text-slate-600">
          Enter your email and we'll send you a reset link
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Email Field */}
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-slate-700">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-slate-400" />
            </div>
            <input
              {...register('email')}
              type="email"
              id="email"
              autoComplete="email"
              className={`
                block w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm
                placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-offset-2
                transition-colors duration-200
                ${
                  errors.email
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : touchedFields.email && !errors.email
                    ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500'
                    : 'border-slate-300 focus:border-teal-500 focus:ring-teal-500'
                }
              `}
              placeholder="Enter your email"
            />
          </div>
          {errors.email && (
            <div className="flex items-center mt-1 text-sm text-red-600">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.email.message}
            </div>
          )}
        </div>

        {/* Submit Error */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-sm text-red-800">{submitError}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!isValid || isSubmitting}
          className={`
            w-full flex justify-center py-3 px-4 border border-transparent rounded-lg
            text-white font-medium transition-all duration-200 transform
            ${
              !isValid || isSubmitting
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-slate-900 hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
            }
          `}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Sending...
            </div>
          ) : (
            'Send reset link'
          )}
        </button>

        {/* Back to Login */}
        <div className="text-center pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onBackToLogin}
            className="inline-flex items-center text-slate-900 hover:text-teal-600 font-medium transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to sign in
          </button>
        </div>
      </form>
    </div>
  );
};



